import { Button, Group, Stack, Text, TextInput } from "@mantine/core";
import { useEffect, useState } from "react";
import {
  EmptyState,
  ErrorState,
  InlineError,
  LoadingSkeleton,
  SectionCard,
} from "../../components/ui";
import { api, type Customer } from "../../lib/api";
import { Page } from "../../shell/Page";

/**
 * Filling in the phone numbers the GST master did not have (Q6).
 *
 * The decision was "import now, collect phones later": all 143 customers came
 * across from GST N Name.xlsx, most without a number, rather than blocking the
 * import on data nobody had yet. This screen is the "later" - and it matters
 * because Marketing cannot collect a payment against a customer they cannot
 * ring.
 *
 * Each row saves on its own. A list of 143 behind one Save button would mean
 * one bad number loses the other 142.
 */
export function MissingPhones() {
  const [customers, setCustomers] = useState<Customer[]>();
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string>();

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string>();
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [done, setDone] = useState<Record<string, string>>({});

  async function load() {
    try {
      const loaded = await api.customersMissingPhone();
      setCustomers(loaded.customers);
      setTotal(loaded.total);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not load the customer list.",
      );
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function save(customer: Customer) {
    const phone = (drafts[customer.id] ?? "").trim();
    if (phone === "") return;
    setSavingId(customer.id);
    setRowError((current) => ({ ...current, [customer.id]: "" }));
    try {
      const { customer: updated } = await api.updateCustomer(customer.id, { phone });
      setDone((current) => ({
        ...current,
        [customer.id]: updated.phone ?? phone,
      }));
      // Drop the row from the list: it is no longer missing a phone, and
      // leaving it makes the remaining count wrong.
      setCustomers((current) => current?.filter((row) => row.id !== customer.id));
      setTotal((current) => Math.max(0, current - 1));
    } catch (caught) {
      setRowError((current) => ({
        ...current,
        [customer.id]:
          caught instanceof Error ? caught.message : "Could not save that number.",
      }));
    } finally {
      setSavingId(undefined);
    }
  }

  const savedCount = Object.keys(done).length;

  return (
    <Page
      title="Missing phone numbers"
      subtitle="Imported customers that cannot be contacted yet"
    >
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!customers && !error && <LoadingSkeleton variant="card" count={4} />}

      {customers && (
        <SectionCard title="Where you are">
          <Stack gap={4}>
            <Text fz="md" c="gray.7">
              {total} customer{total === 1 ? "" : "s"} still without a number
              {customers.length < total && ` · showing the first ${customers.length}`}
            </Text>
            {savedCount > 0 && (
              <Text fz="md" c="teal.8">
                {savedCount} number{savedCount === 1 ? "" : "s"} saved this session.
              </Text>
            )}
            <Text fz="sm" c="gray.6">
              10 digits. +91, spaces and dashes are fine — they are stripped on
              save.
            </Text>
          </Stack>
        </SectionCard>
      )}

      {customers?.length === 0 && (
        <EmptyState
          title="Every customer has a phone number"
          description="Nothing to do here."
        />
      )}

      {customers?.map((customer) => (
        <SectionCard key={customer.id} title={customer.name}>
          <Stack gap="sm">
            <Stack gap={2}>
              <Text fz="sm" c="gray.6">
                {customer.gstNumber ?? "No GSTIN"}
                {customer.state ? ` · ${customer.state}` : ""}
              </Text>
              {customer.address && (
                <Text fz="sm" c="gray.6">
                  {customer.address}
                </Text>
              )}
            </Stack>

            <Group gap="sm" align="flex-end" wrap="nowrap">
              <TextInput
                id={`phone-${customer.id}`}
                label="Phone"
                type="tel"
                inputMode="tel"
                autoComplete="off"
                placeholder="10 digits"
                className="num"
                style={{ flex: 1 }}
                value={drafts[customer.id] ?? ""}
                onChange={(event) =>
                  setDrafts((current) => ({
                    ...current,
                    [customer.id]: event.currentTarget.value,
                  }))
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter") void save(customer);
                }}
              />
              <Button
                loading={savingId === customer.id}
                disabled={(drafts[customer.id] ?? "").trim() === ""}
                onClick={() => void save(customer)}
              >
                Save
              </Button>
            </Group>
            {rowError[customer.id] && (
              <InlineError>{rowError[customer.id]}</InlineError>
            )}
          </Stack>
        </SectionCard>
      ))}

      {customers && total > customers.length && (
        <SectionCard title="More to go">
          <Stack gap="sm">
            <Text fz="md" c="gray.7">
              {total - customers.length} more to go. Save the numbers above,
              then refresh — a customer drops off this list as soon as it has a
              number, so refreshing brings in the next batch.
            </Text>
            {/* Not "load more": this re-runs the same first-50 query rather
                than paging, which is correct here precisely because the list
                shrinks as it is worked through. Paging by offset would skip
                rows as earlier ones disappear. */}
            <Button variant="default" onClick={() => void load()}>
              Refresh the list
            </Button>
          </Stack>
        </SectionCard>
      )}
    </Page>
  );
}
